import React, { ReactElement } from 'react';
import type { Route } from 'next';
import classNames from 'classnames/bind';
import RootLayout from 'layouts/RootLayout';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import Breadcrumbs from 'components/Breadcrumbs';
import proxyAxios from 'api/proxyAxios';
import { generateSchemeObject } from 'sections/Resources/FreightForwarder/schemes';
import { endpoints } from 'api/endpoints';
import { useUtils } from 'utils/hooks/useUtils';
import { CountriesData, CountriesUrls } from 'sections/Resources/FreightForwarder/data';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getCountryContent, getCountryIsoAlpha02, getCountryMetaData } from 'sections/Resources/FreightForwarder/utils';
import styles from 'pages/freight-forwarder/[title]/Landing.module.scss';

const cn = classNames.bind(styles);

const SectionHeader = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionHeader'));
const SectionInstructions = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionInstructions'));
// (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774)
// const SectionCallToAction = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionCallToAction'));
const SectionAbout = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionAbout'));
// (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774)
// const SectionLicense = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionLicense'));
const SectionOtherLocations = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionOtherLocations'));
// (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774)
// const SectionMembers = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionMembers'));
const SectionPosts = dynamic(() => import('sections/Resources/FreightForwarder/landing/components/SectionPosts'));

export default function Landing(props: any) {
  const { logged } = useSelector((state: RootState) => state.userData);
  const FaqScheme = generateSchemeObject(props.pagePath);

  return (
    <div className={cn('landing')}>
      <div className={cn('container')}>
        <Breadcrumbs
          mTop={64}
          mode="light"
          links={[
            { title: 'Freight Forwarder', href: '/freight-forwarder' as Route},
            { title: props.country, href: '' as Route},
          ]}
        />
      </div>

      <SectionHeader country={props.country} countryIsoAlpha02={props.countryIsoAlpha02} />

      <SectionInstructions country={props.country} countryContent={props.countryContent} />

      {props.countryContent
        ?
        <>
          {/* (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774) */}
          {/* <SectionCallToAction /> */}
          <SectionOtherLocations randomCountries={props.randomCountries} />
        </>
        :
        <>
          <SectionAbout country={props.country} />
          {/* (!) New user registration disabled (by the https://searate.atlassian.net/browse/IMPACT-2774) */}
          {/* <SectionLicense /> */}
          {/* <SectionMembers isUserLogged={logged} country={props.country} /> */}
          <SectionPosts articles={props.articles} />
        </>
      }

      {FaqScheme &&
        <Script
          id='faq-scheme'
          type='application/ld+json'
          strategy='beforeInteractive'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(FaqScheme) }}
        />
      }
    </div>
  );
};

export const getServerSideProps = async (context: any) => {
  const { parseCountries, getRandomCountries } = useUtils();
  const url = context.params.title;
  const user = context.params.user;
  const isValidUrl = CountriesUrls.some((item: any) => item.url === url);

  // For rendering random three countries' urls in 'Other Locations' section: 
  const randomCountries = getRandomCountries(CountriesData, 3);

  const CountryContent = await getCountryContent(CountriesUrls, url);
  const countryIsoAlpha02 = await getCountryIsoAlpha02(CountriesUrls, url);
  const CountryMetaData = await getCountryMetaData(CountriesUrls, url);

  if (!isValidUrl) {
    return {
      redirect: {
        destination: '/freight-forwarder',
        statusCode: 301,
      }
    }
  }

  const country = parseCountries(url);

  const DefaultMetaData = {
    title: (country: string) => `How to Become a Freight Forwarder in ${country} | DF Alliance`,
    description: (country: string) => `Get a step-by-step guide with DF Alliance to become a freight forwarder in ${country}. Get all the basic information, license requirements, and instructions.`,
  };

  const meta: any = {
    title: CountryMetaData.title ? CountryMetaData.title : DefaultMetaData.title(country),
    description: CountryMetaData.description ? CountryMetaData.description : DefaultMetaData.description(country),
    keywords: `How to become a freight forwarder in ${country}`,
    path: `/freight-forwarder/${url}`,
  };

  const pageData: any = {
    country: country,
    countryContent: CountryContent ?? null,
    countryIsoAlpha02: countryIsoAlpha02,
    user: user === undefined ? 'no-user' : 'user',
    pagePath: url,
  };

  try {
    const { data: articles } = await proxyAxios(endpoints.blog.previewArticles + '?limit=3&order=random');

    return {
      props:
      {
        articles,
        randomCountries,
        ...meta,
        ...pageData
      }
    };
  } catch (e) {
    console.log('endpoints.blog.previewArticles', e);
  }

  return {
    props: {
      articles: [],
      randomCountries,
      ...meta,
      ...pageData,
    }
  }
};

Landing.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};
